import * as Joi from "joi";
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	OneToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import { FIELD_SETTING_DATA } from './data';
import Base from '../base.entity';
import User from '../users/user.entity';

// NOTE: created as a database trigger on user insert
// and deleted as a database trigger before user delete

@Entity('inventory_field_settings')
export default class InventoryFieldSetting extends Base {
	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	user_id: number;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	name: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	description: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	brand: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	color: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	size: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	asset_num: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	unit_price: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	total_cost: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	quantity: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	quantity_notes: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	purchase_info: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	disposition_notes: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	hero: boolean;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment | Date | string;

	@Column('int')
	last_updated_user: number;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment | Date | string;

	@OneToOne((type) => User, (user) => user.inventoryFieldSetting)
	@JoinColumn({
		name: 'user_id',
	})
	user: User;

	static rules() {
		return {
			id: Joi.number().integer(),
			user_id: Joi.number().integer(),
			name: Joi.boolean().allow(null),
			description: Joi.boolean().allow(null),
			brand: Joi.boolean(),
			color: Joi.boolean(),
			size: Joi.boolean(),
			asset_num: Joi.boolean(),
			unit_price: Joi.boolean(),
			total_cost: Joi.boolean(),
			quantity: Joi.boolean(),
			quantity_notes: Joi.boolean(),
			purchase_info: Joi.boolean(),
			hero: Joi.boolean(),
			disposition_notes: Joi.boolean(),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			date_created: Joi.date().allow(null),
			created_by: Joi.number().integer(),
		};
	}

	static getDisplayName(field: string) {
		return FIELD_SETTING_DATA[field].label || '';
	}

	toJSON() {
		return {
			id: this.id,
			user_id: this.user_id,
			name: this.name,
			description: this.description,
			brand: this.brand,
			color: this.color,
			size: this.size,
			asset_num: this.asset_num,
			unit_price: this.unit_price,
			total_cost: this.total_cost,
			quantity: this.quantity,
			quantity_notes: this.quantity_notes,
			purchase_info: this.purchase_info,
			disposition_notes: this.disposition_notes,
			hero: this.hero,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			date_created: this.date_created,
			created_by: this.created_by,
		};
	}

	assign(props?: GenPartial<InventoryFieldSetting>) {
		super.assign(props);

		if (props) {
			if (typeof this.name !== 'undefined') {
				this.name = booleanTransformer.from(props.name);
			}
			if (typeof this.description !== 'undefined') {
				this.description = booleanTransformer.from(props.description);
			}
			if (typeof this.brand !== 'undefined') {
				this.brand = booleanTransformer.from(props.brand);
			}
			if (typeof this.color !== 'undefined') {
				this.color = booleanTransformer.from(props.color);
			}
			if (typeof this.size !== 'undefined') {
				this.size = booleanTransformer.from(props.size);
			}
			if (typeof this.asset_num !== 'undefined') {
				this.asset_num = booleanTransformer.from(props.asset_num);
			}
			if (typeof this.unit_price !== 'undefined') {
				this.unit_price = booleanTransformer.from(props.unit_price);
			}
			if (typeof this.total_cost !== 'undefined') {
				this.total_cost = booleanTransformer.from(props.total_cost);
			}
			if (typeof this.quantity !== 'undefined') {
				this.quantity = booleanTransformer.from(props.quantity);
			}
			if (typeof this.quantity_notes !== 'undefined') {
				this.quantity_notes = booleanTransformer.from(props.quantity_notes);
			}
			if (typeof this.purchase_info !== 'undefined') {
				this.purchase_info = booleanTransformer.from(props.purchase_info);
			}
			if (typeof this.disposition_notes !== 'undefined') {
				this.disposition_notes = booleanTransformer.from(
					props.disposition_notes
				);
			}
			if (typeof this.hero !== 'undefined') {
				this.hero = booleanTransformer.from(props.hero);
			}
			if (typeof this.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof this.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<InventoryFieldSetting>) {
		super(props);
	}

	update(payload: Partial<InventoryFieldSetting>) {
		for (const field in FIELD_SETTING_DATA) {
			if (
				FIELD_SETTING_DATA.hasOwnProperty(field) &&
				typeof payload[field] !== 'undefined'
			) {
				this[field] = payload[field];
			}
		}
	}
}
